import dashboardMenu from '../menu-items/dashboard';
import stackingMenu from '../menu-items/page';

const SKIPPED_MENU_IDS = new Set(['login', 'dashboard', 'admins']);

const mapMenuItem = (item) => {
  if (item.type === 'collapse' && item.children?.length) {
    const children = item.children.map(mapMenuItem).filter(Boolean);
    if (!children.length) return null;
    return { label: item.title, children };
  }

  if (item.permissionKey) {
    return { key: item.permissionKey, label: item.title };
  }

  return null;
};

const buildPermissionTreeFromSidebar = () => {
  const navigationChildren = dashboardMenu.children
    .filter((item) => item.permissionKey)
    .map((item) => ({ key: item.permissionKey, label: item.title }));

  const stackingChildren = stackingMenu.children
    .filter((item) => !SKIPPED_MENU_IDS.has(item.id) && !item.adminOnly)
    .map(mapMenuItem)
    .filter(Boolean);

  return [
    { label: dashboardMenu.title, children: navigationChildren },
    { label: stackingMenu.title, children: stackingChildren }
  ];
};

/** Sidebar-aligned tree: Navigation + STACKING sections with all permission keys */
export const PERMISSION_TREE = buildPermissionTreeFromSidebar();

const LEGACY_PERMISSION_MAP = {
  userDetails: 'newKaitUsers',
  complaint: 'complaintsReport',
  oldUsersReport: 'oldUserReport'
};

export const collectPermissionKeys = (tree = PERMISSION_TREE) => {
  const keys = [];
  tree.forEach((node) => {
    if (node.key) keys.push(node.key);
    if (node.children) keys.push(...collectPermissionKeys(node.children));
  });
  return keys;
};

const buildPermissionLabels = (tree = PERMISSION_TREE) => {
  const labels = {};
  const walk = (nodes) => {
    nodes.forEach((node) => {
      if (node.key) labels[node.key] = node.label;
      if (node.children) walk(node.children);
    });
  };
  walk(tree);
  return labels;
};

export const PERMISSION_LABELS = buildPermissionLabels();

export const PERMISSION_MODULES = Object.fromEntries(
  collectPermissionKeys().map((key) => [key, { read: false, write: false }])
);

const resolvePermissionKey = (rawKey) => LEGACY_PERMISSION_MAP[rawKey] || rawKey;

const applyFlatPermissionList = (list, base) => {
  list.forEach((entry) => {
    if (typeof entry !== 'string') return;

    const readMatch = entry.match(/^(.+)_read$/);
    const writeMatch = entry.match(/^(.+)_write$/);

    if (readMatch) {
      const key = resolvePermissionKey(readMatch[1]);
      if (base[key]) base[key].read = true;
    }

    if (writeMatch) {
      const key = resolvePermissionKey(writeMatch[1]);
      if (base[key]) base[key].write = true;
    }
  });
};

const applyPermissionEntry = (rawKey, val, base) => {
  const key = resolvePermissionKey(rawKey);
  if (!base[key]) return;

  if (Array.isArray(val)) {
    base[key].read = val.includes('read');
    base[key].write = val.includes('write');
  } else if (val && typeof val === 'object') {
    base[key].read = Boolean(val.read);
    base[key].write = Boolean(val.write);
  }
};

export const normalizePermissions = (value) => {
  const base = Object.fromEntries(
    Object.entries(PERMISSION_MODULES).map(([key, defaults]) => [key, { ...defaults }])
  );

  if (!value) return base;

  let parsed = value;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return base;
    }
  }

  if (Array.isArray(parsed)) {
    applyFlatPermissionList(parsed, base);
    return base;
  }

  if (typeof parsed !== 'object') return base;

  Object.entries(parsed).forEach(([rawKey, val]) => applyPermissionEntry(rawKey, val, base));

  return base;
};

const isAdminRole = (role) => {
  if (!role) return false;
  const normalized = role.toString().trim().toLowerCase();
  return normalized === 'admin' || normalized === 'superadmin' || normalized === 'super admin';
};

export const grantAllPermissions = () =>
  Object.fromEntries(collectPermissionKeys().map((key) => [key, { read: true, write: true }]));

export const formatPermissionsSummary = (permissions, role) => {
  if (isAdminRole(role)) return 'Full access — all sidebar modules';

  const normalized = normalizePermissions(permissions);
  const parts = [];

  Object.entries(normalized).forEach(([key, val]) => {
    const access = [];
    if (val.read) access.push('read');
    if (val.write) access.push('write');
    if (access.length) {
      const label = PERMISSION_LABELS[key] || key;
      parts.push(`${label}: ${access.join(', ')}`);
    }
  });

  return parts.length ? parts.join(' | ') : '—';
};
